/**
 * KinsCircle Phase 6D — Cinematic Story Playback Modal.
 *
 * STABILITY.md §13–§14:
 *   - Read-only presentation of slides supplied by the caller. NEVER
 *     mutates the underlying memories. NEVER auto-saves anything.
 *   - Renders null when `storyPlayback` flag is OFF, so it is safe to
 *     mount unconditionally from any caller.
 *   - No audio. No autoplay videos. No external media libraries.
 *   - Honors the safety contract: each slide must already include the
 *     "Based on N memor(ies) from YEAR–YEAR" citation.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Pause,
  Play,
  ChevronLeft,
  ChevronRight,
  X,
  Calendar,
  Users,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import { isStoryPlaybackEnabled } from '@/lib/ai/featureFlag';
import type { LegacyTimelineSlide } from '@/lib/ai/legacyTimelineEngine';

export type PlaybackStory = {
  /** Section / story label shown in the header. */
  label: string;
  /** Optional subtitle (e.g. "Based on 24 memories from 2008–2023"). */
  subtitle?: string | null;
  slides: LegacyTimelineSlide[];
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  story: PlaybackStory | null;
  /** Optional callback when the user taps "View original memories". */
  onOpenOriginals?: (memoryIds: string[]) => void;
};

const AUTO_ADVANCE_MS = 6500;

const StoryPlaybackModal: React.FC<Props> = ({
  open,
  onOpenChange,
  story,
  onOpenOriginals,
}) => {
  // Hard gate: no flag → render nothing (and never run any timers).
  if (!isStoryPlaybackEnabled()) return null;

  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [fadeKey, setFadeKey] = useState(0);
  const timerRef = useRef<number | null>(null);

  const slides = useMemo(() => story?.slides ?? [], [story]);
  const total = slides.length;
  const slide = slides[Math.min(index, Math.max(0, total - 1))];

  // Reset whenever a new story is loaded or the modal reopens.
  useEffect(() => {
    if (!open) return;
    setIndex(0);
    setPlaying(true);
    setFadeKey((k) => k + 1);
  }, [open, story?.label]);

  const goNext = useCallback(() => {
    if (!total) return;
    setIndex((i) => (i + 1 < total ? i + 1 : i));
    setFadeKey((k) => k + 1);
  }, [total]);

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
    setFadeKey((k) => k + 1);
  }, []);

  // Auto-advance timer. Pauses on `playing=false` or last slide.
  useEffect(() => {
    if (!open || !playing || !total) return;
    if (index >= total - 1) return;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      goNext();
    }, AUTO_ADVANCE_MS);
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open, playing, index, total, goNext, fadeKey]);

  // Keyboard nav.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === ' ') {
        e.preventDefault();
        setPlaying((p) => !p);
      } else if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, goNext, goPrev, onOpenChange]);

  if (!story || !slide) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <div className="text-center py-8 text-slate-600">
            No memories yet to play back.
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const progressPct = total > 1 ? ((index + 1) / total) * 100 : 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl w-[95vw] p-0 overflow-hidden bg-slate-900 text-white border-0 [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Soft animated gradient background — safely deterministic. */}
        <div
          key={fadeKey}
          className={[
            'relative min-h-[460px] md:min-h-[540px] flex flex-col',
            'bg-gradient-to-br',
            slide.toneGradient,
            'animate-in fade-in duration-500',
          ].join(' ')}
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-5 pt-5">
            <div className="flex items-center gap-2 text-slate-900/80">
              <Sparkles className="w-4 h-4" />
              <div>
                <div className="text-sm font-semibold leading-tight">
                  {story.label}
                </div>
                {story.subtitle && (
                  <div className="text-[11px] opacity-70 leading-tight">
                    {story.subtitle}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              className="p-2 rounded-full hover:bg-white/40 text-slate-900"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Progress bar */}
          <div className="px-5 mt-3">
            <div className="h-1 w-full rounded-full bg-white/40 overflow-hidden">
              <div
                className="h-full bg-slate-900/70 transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {/* Slide body */}
          <div className="flex-1 flex items-center justify-center px-6 py-10">
            <div className="max-w-xl text-center text-slate-900">
              {slide.year && (
                <Badge className="bg-white/70 text-slate-800 border border-white/60 hover:bg-white/70 mb-3">
                  <Calendar className="w-3.5 h-3.5 mr-1" />
                  {slide.year}
                </Badge>
              )}
              <h2 className="font-serif text-2xl md:text-3xl font-bold leading-tight mb-3">
                {slide.title}
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-slate-800/90 whitespace-pre-wrap">
                {slide.body}
              </p>
              {slide.taggedNames.length > 0 && (
                <div className="mt-4 flex items-center justify-center gap-1.5 text-sm text-slate-800/85">
                  <Users className="w-4 h-4" />
                  <span className="truncate">
                    {slide.taggedNames.slice(0, 4).join(', ')}
                    {slide.taggedNames.length > 4 &&
                      ` +${slide.taggedNames.length - 4} more`}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Footer controls + citation */}
          <div className="bg-slate-900/85 backdrop-blur px-5 py-3 flex flex-col gap-2.5">
            <div className="text-[11px] text-white/70 italic text-center">
              {slide.citation}
            </div>
            <div className="flex items-center justify-between gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={goPrev}
                disabled={index === 0}
                className="text-white hover:bg-white/10 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Prev
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setPlaying((p) => !p)}
                  aria-label={playing ? 'Pause' : 'Play'}
                  className="text-white hover:bg-white/10 h-9 w-9"
                >
                  {playing ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                </Button>
                <span className="text-xs text-white/70 tabular-nums">
                  {index + 1} / {total}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={goNext}
                disabled={index >= total - 1}
                className="text-white hover:bg-white/10 disabled:opacity-40"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            {onOpenOriginals && slide.sourceMemoryIds.length > 0 && (
              <button
                onClick={() => onOpenOriginals(slide.sourceMemoryIds)}
                className="text-xs text-white/80 hover:text-white inline-flex items-center justify-center gap-1.5"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                View original memor
                {slide.sourceMemoryIds.length === 1 ? 'y' : 'ies'}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StoryPlaybackModal;
